/* ==========================================================================
   fm-global.js — FM enhancement runtime for pctechsupply.com (Ella 6.4.0)
   --------------------------------------------------------------------------
   Loaded with defer AFTER theme.js, so jQuery, window.routes, window.money_format
   and window.fm are all guaranteed to exist. Every feature is guarded so a
   failure in one never breaks the others or the host theme.
   ========================================================================== */
(function () {
  'use strict';

  var FM = (window.fm = window.fm || {});
  var $ = window.jQuery;

  /* ----------------------------------------------------------------------
     Utilities
     ---------------------------------------------------------------------- */
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function safe(name, fn) {
    try { fn(); } catch (e) { if (window.console) console.warn('[fm] ' + name + ' failed:', e); }
  }

  function formatMoney(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.money_format);
      }
    } catch (e) {}
    var v = (cents / 100).toFixed(2);
    return (window.money_format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, v).replace(/\$?\{\{.*?\}\}/, v);
  }

  /* ----------------------------------------------------------------------
     1. Dark-mode toggle
     ----------------------------------------------------------------------
     The <html data-fm-theme> attribute is set pre-paint by the inline
     bootstrap in fm-head.liquid (no FOUC). Here we only wire the buttons. */
  function initDarkToggle() {
    var cfg = FM.theme || { storageKey: 'fm-theme', default: 'light' };
    // If dark mode is disabled in theme settings, do nothing at all — never
    // touch data-fm-theme, so the store keeps its stock (light) palette.
    if (cfg.enabled === false) return;
    var root = document.documentElement;

    function current() {
      return root.getAttribute('data-fm-theme') === 'dark' ? 'dark' : 'light';
    }
    function apply(mode, persist) {
      root.setAttribute('data-fm-theme', mode);
      if (persist) {
        try { localStorage.setItem(cfg.storageKey, mode); } catch (e) {}
      }
      document.querySelectorAll('.fm-dark-toggle').forEach(function (btn) {
        btn.setAttribute('aria-pressed', String(mode === 'dark'));
      });
    }
    // Sync any late-rendered toggle to current state.
    apply(current(), false);

    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('.fm-dark-toggle') : null;
      if (!btn) return;
      e.preventDefault();
      apply(current() === 'dark' ? 'light' : 'dark', true);
    });
  }

  /* ----------------------------------------------------------------------
     2. Focus-visible enablement (keyboard users only)
     ---------------------------------------------------------------------- */
  function initFocusVisible() {
    function on(e) {
      if (e.key === 'Tab') {
        document.body.classList.add('fm-focus-enabled');
        window.removeEventListener('keydown', on);
      }
    }
    window.addEventListener('keydown', on);
  }

  /* ----------------------------------------------------------------------
     3. Keyboard-navigable desktop mega/dropdown menus
     ----------------------------------------------------------------------
     Ella opens submenus on :hover only. Add aria + a class toggled by focus
     and Enter/Space/Escape so keyboard users can reach level 2/3. */
  function initNavKeyboard() {
    if (window.matchMedia && !window.matchMedia('(min-width: 1025px)').matches) return;
    var nav = document.getElementById('HeaderNavigation');
    if (!nav) return;

    var parents = nav.querySelectorAll('.menu-lv-item.has-megamenu, .menu-lv-item.has-submenu, li.has-children');
    parents.forEach(function (li) {
      var link = li.querySelector(':scope > a, :scope > .menu-lv-link, a');
      var sub = li.querySelector('.menu-dropdown, .sub-menu, ul');
      if (!link || !sub) return;
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');

      function open() {
        li.classList.add('fm-focus-open');
        link.setAttribute('aria-expanded', 'true');
      }
      function close() {
        li.classList.remove('fm-focus-open');
        link.setAttribute('aria-expanded', 'false');
      }
      li.addEventListener('focusin', open);
      li.addEventListener('focusout', function (e) {
        if (!li.contains(e.relatedTarget)) close();
      });
      link.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          if (link.getAttribute('href') === '#' || !link.getAttribute('href')) e.preventDefault();
          li.classList.contains('fm-focus-open') ? close() : open();
        } else if (e.key === 'Escape') {
          close();
          link.focus();
        }
      });
    });
  }

  /* ----------------------------------------------------------------------
     4. Wishlist hearts on AJAX/JS-rendered cards
     ----------------------------------------------------------------------
     Ella persists a localStorage array of handles under 'wishlistItem'.
     Some card surfaces (halo-product-card / ?view=ajax_card, recently-viewed)
     ship no heart. Inject one and reuse the same store + [data-wishlist] class
     contract so Ella's own hearts stay in sync. */
  var WL_KEY = 'wishlistItem';
  function wlRead() {
    try { return JSON.parse(localStorage.getItem(WL_KEY)) || []; } catch (e) { return []; }
  }
  function wlWrite(arr) {
    try { localStorage.setItem(WL_KEY, JSON.stringify(arr)); } catch (e) {}
  }
  function wlHas(handle) { return wlRead().indexOf(handle) !== -1; }
  function wlToggle(handle) {
    var arr = wlRead();
    var i = arr.indexOf(handle);
    if (i === -1) arr.push(handle); else arr.splice(i, 1);
    wlWrite(arr);
    updateWishlistCount();
    return i === -1; // true if now added
  }
  function updateWishlistCount() {
    var n = wlRead().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) { el.textContent = n; });
  }

  function heartSVG() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
      '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>';
  }

  function injectHearts(root) {
    if (!FM.wishlist || FM.wishlist.enabled === false) return;
    root = root || document;
    var cards = root.querySelectorAll('.product-item[data-product-id], .card-product[data-product-id]');
    cards.forEach(function (card) {
      if (card.querySelector('[data-wishlist], .fm-card-heart')) return; // already has one
      var handle = card.getAttribute('data-product-handle');
      if (!handle) {
        var a = card.querySelector('a.card-title[data-product-url], a.card-media[href]');
        if (a) {
          var href = a.getAttribute('data-product-url') || a.getAttribute('href') || '';
          var m = href.match(/\/products\/([^/?#]+)/);
          if (m) handle = m[1];
        }
      }
      if (!handle) return;
      var wrap = card.querySelector('.card-product__wrapper, .card-media') || card;
      if (getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
      var btn = document.createElement('a');
      btn.href = '#';
      btn.className = 'fm-card-heart' + (wlHas(handle) ? ' wishlist-added' : '');
      btn.setAttribute('data-fm-wishlist', handle);
      btn.setAttribute('role', 'button');
      btn.setAttribute('aria-label', (FM.strings && FM.strings.wishlistAdd) || 'Add to wishlist');
      btn.innerHTML = heartSVG();
      wrap.appendChild(btn);
    });
  }

  function initWishlistHearts() {
    updateWishlistCount();
    injectHearts(document);
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-fm-wishlist]') : null;
      if (!btn) return;
      e.preventDefault();
      var handle = btn.getAttribute('data-fm-wishlist');
      var added = wlToggle(handle);
      btn.classList.toggle('wishlist-added', added);
      // keep Ella's native hearts for the same product in sync visually
      document.querySelectorAll('[data-wishlist-handle="' + handle + '"]').forEach(function (el) {
        el.classList.toggle('wishlist-added', added);
      });
    });
  }

  /* ----------------------------------------------------------------------
     5. Free-shipping progress bar (fm-shipbar)
     ----------------------------------------------------------------------
     Independent of Ella's misconfigured calculator. Reads /cart.js and
     updates every fm-shipbar on the page + after every cart mutation. */
  function renderShipbars(cart) {
    var cfg = FM.freeShip;
    if (!cfg || !cfg.enabled) return;
    var threshold = cfg.threshold; // cents
    if (!threshold || threshold <= 0) return;
    var total = cart ? cart.total_price : 0;
    var remaining = Math.max(threshold - total, 0);
    var pct = Math.min(100, Math.round((total / threshold) * 100));

    document.querySelectorAll('.fm-shipbar').forEach(function (bar) {
      var fill = bar.querySelector('.fm-shipbar__fill');
      var msg = bar.querySelector('.fm-shipbar__message');
      if (fill) fill.style.width = pct + '%';
      if (msg) {
        if (remaining <= 0) {
          bar.classList.add('fm-shipbar--done');
          msg.innerHTML = cfg.msgDone || 'You have unlocked free shipping!';
        } else {
          bar.classList.remove('fm-shipbar--done');
          var tmpl = cfg.msgRemaining || "You're {amount} away from FREE shipping!";
          msg.innerHTML = tmpl.replace('{amount}', '<strong>' + formatMoney(remaining) + '</strong>');
        }
      }
      var pb = bar.querySelector('.fm-shipbar__track');
      if (pb) {
        pb.setAttribute('role', 'progressbar');
        pb.setAttribute('aria-valuemin', '0');
        pb.setAttribute('aria-valuemax', '100');
        pb.setAttribute('aria-valuenow', String(pct));
      }
    });
  }

  function fetchCartThen(fn) {
    fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(fn)
      .catch(function () {});
  }

  function initShipbar() {
    if (!FM.freeShip || !FM.freeShip.enabled) return;
    if (!document.querySelector('.fm-shipbar')) return;
    fetchCartThen(renderShipbars);
  }

  /* ----------------------------------------------------------------------
     6. Cart-change event bridge
     ----------------------------------------------------------------------
     Ella ships no pubsub. Re-publish jQuery ajax cart mutations as a DOM
     CustomEvent and refresh fm surfaces (shipbar, wishlist hearts in drawer). */
  function initCartBridge() {
    function refresh() {
      safe('shipbar-refresh', function () { fetchCartThen(renderShipbars); });
      safe('drawer-hearts', function () {
        var drawer = document.querySelector('#halo-cart-sidebar, .halo-cart-sidebar');
        if (drawer) injectHearts(drawer);
      });
      document.dispatchEvent(new CustomEvent('fm:cart:updated'));
    }
    if ($) {
      $(document).ajaxComplete(function (e, xhr, s) {
        var url = (s && s.url) || '';
        if (/\/cart\/(add|change|update|clear)(\.js)?|cart\?view=ajax_side_cart|\/cart\.js/.test(url)) {
          setTimeout(refresh, 60);
        }
      });
    }
    // Also cover fetch()-based product forms.
    document.addEventListener('submit', function (e) {
      if (e.target && e.target.matches && e.target.matches('form[action*="/cart/add"]')) {
        setTimeout(refresh, 400);
      }
    }, true);
  }

  /* ----------------------------------------------------------------------
     7. Low-stock live update on PDP variant change
     ----------------------------------------------------------------------
     fm-low-stock.liquid renders the initial state server-side. When the
     shopper switches variant, recompute from window.product_inven_array_<id>. */
  function initLowStockLive() {
    document.querySelectorAll('.fm-lowstock[data-fm-lowstock]').forEach(function (el) {
      var pid = el.getAttribute('data-product-id');
      var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 0;
      var form = el.closest('.productView, [data-section-type], form') || document;
      function update() {
        var arr = window['product_inven_array_' + pid];
        if (!arr) return;
        var idInput = document.querySelector('input[name="id"]:checked, select[name="id"], input[name="id"]');
        var vid = idInput ? parseInt(idInput.value, 10) : null;
        var qty = null;
        for (var i = 0; i < arr.length; i++) {
          if (arr[i] && parseInt(arr[i].id, 10) === vid) { qty = parseInt(arr[i].inventory_quantity, 10); break; }
        }
        if (qty === null) return;
        if (qty > 0 && qty <= threshold) {
          el.hidden = false;
          var n = el.querySelector('[data-fm-lowstock-count]');
          if (n) n.textContent = qty;
        } else {
          el.hidden = true;
        }
      }
      form.addEventListener('change', function (e) {
        if (e.target && e.target.name === 'id') update();
      });
      document.addEventListener('variant:changed', update);
    });
  }

  /* ----------------------------------------------------------------------
     8. Countdown sections (fm-countdown) — auto-hide on expiry
     ---------------------------------------------------------------------- */
  function initCountdowns() {
    var timers = document.querySelectorAll('.fm-countdown[data-deadline]');
    if (!timers.length) return;
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function tick() {
      var now = Date.now();
      timers.forEach(function (t) {
        var deadline = parseInt(t.getAttribute('data-deadline'), 10);
        if (!deadline) return;
        var diff = deadline - now;
        if (diff <= 0) {
          if (t.getAttribute('data-hide-on-end') !== 'false') t.hidden = true;
          return;
        }
        var s = Math.floor(diff / 1000);
        var d = Math.floor(s / 86400); s -= d * 86400;
        var h = Math.floor(s / 3600); s -= h * 3600;
        var m = Math.floor(s / 60); s -= m * 60;
        var set = function (sel, val) { var el = t.querySelector(sel); if (el) el.textContent = val; };
        set('[data-cd="days"]', d);
        set('[data-cd="hours"]', pad(h));
        set('[data-cd="mins"]', pad(m));
        set('[data-cd="secs"]', pad(s));
      });
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ----------------------------------------------------------------------
     9. Frequently Bought Together (fm-fbt)
     ----------------------------------------------------------------------
     Fetch complementary recommendations; fall back to related when empty.
     Renders a checkbox list + running total + single add-to-cart. */
  function initFBT() {
    var host = document.querySelector('.fm-fbt[data-product-id]');
    if (!host || !FM.fbt || FM.fbt.enabled === false) return;
    var pid = host.getAttribute('data-product-id');
    var limit = parseInt(host.getAttribute('data-limit'), 10) || (FM.fbt.limit || 4);
    var base = (window.routes && window.routes.product_recommendations_url) || '/recommendations/products';

    function url(intent) {
      return base + '?product_id=' + pid + '&limit=' + limit + '&intent=' + intent + '&section_id=';
    }
    function fetchJSON(intent) {
      return fetch('/recommendations/products.json?product_id=' + pid + '&limit=' + limit + '&intent=' + intent, {
        headers: { 'Accept': 'application/json' }
      }).then(function (r) { return r.json(); });
    }

    fetchJSON('complementary')
      .then(function (data) {
        if (data && data.products && data.products.length) return data.products;
        return fetchJSON('related').then(function (d) { return (d && d.products) || []; });
      })
      .then(function (products) {
        if (!products.length) { host.hidden = true; return; }
        renderFBT(host, products);
      })
      .catch(function () { host.hidden = true; });
  }

  function renderFBT(host, products) {
    var thisTitle = host.getAttribute('data-this-title') || 'This item';
    var thisPrice = parseInt(host.getAttribute('data-this-price'), 10) || 0;
    var thisImg = host.getAttribute('data-this-image') || '';
    var addText = (FM.fbt && FM.fbt.addText) || 'Add selected to cart';

    var picks = products.slice(0, 3);
    var strip = '<div class="fm-fbt__thumb">' + (thisImg ? '<img src="' + thisImg + '" alt="" loading="lazy" width="84" height="84">' : '') + '</div>';
    var rows = '<li class="fm-fbt__row"><input type="checkbox" checked disabled aria-label="' + escapeHtml(thisTitle) + '">' +
      '<label class="fm-fbt__this">' + escapeHtml(thisTitle) + ' <em>(this item)</em></label>' +
      '<span class="fm-fbt__price">' + formatMoney(thisPrice) + '</span></li>';

    picks.forEach(function (p, i) {
      var img = (p.featured_image || (p.images && p.images[0]) || '');
      var vid = p.variants && p.variants[0] ? p.variants[0].id : null;
      var price = p.price != null ? p.price : (p.variants && p.variants[0] ? p.variants[0].price : 0);
      strip += '<span class="fm-fbt__plus" aria-hidden="true">+</span>' +
        '<div class="fm-fbt__thumb" data-idx="' + i + '">' + (img ? '<img src="' + img + '" alt="" loading="lazy" width="84" height="84">' : '') + '</div>';
      rows += '<li class="fm-fbt__row">' +
        '<input type="checkbox" checked data-fm-fbt-item data-variant="' + vid + '" data-price="' + price + '" data-idx="' + i + '" aria-label="' + escapeHtml(p.title) + '">' +
        '<label><a href="' + p.url + '">' + escapeHtml(p.title) + '</a></label>' +
        '<span class="fm-fbt__price">' + formatMoney(price) + '</span></li>';
    });

    host.innerHTML =
      '<h2 class="fm-fbt__heading">' + escapeHtml((FM.fbt && FM.fbt.heading) || 'Frequently bought together') + '</h2>' +
      '<div class="fm-fbt__strip">' + strip + '</div>' +
      '<ul class="fm-fbt__list">' + rows + '</ul>' +
      '<div class="fm-fbt__footer">' +
      '<span class="fm-fbt__total">Total: <strong data-fm-fbt-total></strong></span>' +
      '<button type="button" class="fm-fbt__add" data-fm-fbt-add>' + escapeHtml(addText) + '</button>' +
      '</div>';
    host.hidden = false;

    function recompute() {
      var total = thisPrice;
      host.querySelectorAll('[data-fm-fbt-item]').forEach(function (cb) {
        var idx = cb.getAttribute('data-idx');
        var thumb = host.querySelector('.fm-fbt__thumb[data-idx="' + idx + '"]');
        if (cb.checked) { total += parseInt(cb.getAttribute('data-price'), 10) || 0; if (thumb) thumb.classList.remove('fm-fbt__thumb--off'); }
        else if (thumb) { thumb.classList.add('fm-fbt__thumb--off'); }
      });
      var t = host.querySelector('[data-fm-fbt-total]');
      if (t) t.textContent = formatMoney(total);
    }
    recompute();
    host.addEventListener('change', function (e) {
      if (e.target && e.target.hasAttribute('data-fm-fbt-item')) recompute();
    });

    host.querySelector('[data-fm-fbt-add]').addEventListener('click', function () {
      var items = [];
      host.querySelectorAll('[data-fm-fbt-item]:checked').forEach(function (cb) {
        var vid = cb.getAttribute('data-variant');
        if (vid && vid !== 'null') items.push({ id: parseInt(vid, 10), quantity: 1 });
      });
      if (!items.length) return;
      var btn = this;
      btn.disabled = true;
      fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) { return r.json(); })
        .then(function () {
          if ($) $(document).trigger('ajaxComplete', [null, { url: '/cart/add.js' }]);
          var trigger = document.querySelector('[data-cart-sidebar], #cart-icon-bubble');
          if (trigger) trigger.click();
          btn.disabled = false;
        })
        .catch(function () { btn.disabled = false; });
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ----------------------------------------------------------------------
     10. FM Skin scroll reveal — product cards rise in on scroll.
     ----------------------------------------------------------------------
     Only runs when the skin is on, IntersectionObserver exists, and motion is
     allowed. Adds html.fm-reveal so the hidden state applies; a fail-safe
     timer reveals everything after 2s so content can never stay hidden. */
  function initSkinReveal() {
    var root = document.documentElement;
    if (root.getAttribute('data-fm-skin') !== 'on') return;
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    root.classList.add('fm-reveal');
    var cards = document.querySelectorAll('.product-item');
    if (!cards.length) { root.classList.remove('fm-reveal'); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('fm-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
    cards.forEach(function (c) { io.observe(c); });

    // Fail-safe: never leave a card hidden.
    setTimeout(function () {
      document.querySelectorAll('.product-item:not(.fm-in)').forEach(function (c) { c.classList.add('fm-in'); });
    }, 2000);
  }

  /* ----------------------------------------------------------------------
     11. FM Hero premium motion — canvas particle field, count-up stats,
         magnetic CTA, parallax chips. All decorative; the hero looks complete
         without any of it (static gradient + text), so failure is invisible.
     ---------------------------------------------------------------------- */
  var motionOK = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function initFmHero() {
    var heroes = document.querySelectorAll('.fm-hero');
    if (!heroes.length) return;
    heroes.forEach(function (hero) {
      // Count-up stat numbers (parse a leading number, keep the suffix).
      hero.querySelectorAll('.fm-hero__n').forEach(function (el) {
        var raw = (el.textContent || '').trim();
        var m = raw.match(/^([\d.,]+)(.*)$/);
        if (!m) return;
        var target = parseFloat(m[1].replace(/,/g, ''));
        if (isNaN(target)) return;
        var suffix = m[2] || '';
        var dec = (m[1].split('.')[1] || '').length;
        if (!motionOK || !('IntersectionObserver' in window)) return; // leave text as-authored
        var run = function () {
          var t0 = null;
          (function step(ts) {
            if (!t0) t0 = ts;
            var k = Math.min((ts - t0) / 1300, 1);
            var e = 1 - Math.pow(1 - k, 3);
            var v = target * e;
            el.textContent = (dec ? v.toFixed(dec) : Math.round(v).toLocaleString()) + suffix;
            if (k < 1) requestAnimationFrame(step);
          })(0 + performance.now());
        };
        var io = new IntersectionObserver(function (es) {
          es.forEach(function (e) { if (e.isIntersecting) { run(); io.unobserve(e.target); } });
        }, { threshold: 0.6 });
        io.observe(el);
      });

      if (!motionOK) return; // no continuous/pointer motion under reduced-motion

      // Magnetic primary button (desktop pointers only).
      if (finePointer) {
        var btn = hero.querySelector('.fm-hero__btn--primary');
        if (btn) {
          btn.addEventListener('pointermove', function (e) {
            var r = btn.getBoundingClientRect();
            btn.style.transform = 'translate(' + (e.clientX - r.left - r.width / 2) * 0.25 + 'px,' + (e.clientY - r.top - r.height / 2) * 0.4 + 'px)';
          });
          btn.addEventListener('pointerleave', function () {
            btn.style.transition = 'transform .5s cubic-bezier(.34,1.56,.5,1)';
            btn.style.transform = '';
            setTimeout(function () { btn.style.transition = ''; }, 500);
          });
        }
        // Parallax chips.
        var chips = hero.querySelectorAll('.fm-hero__chip');
        if (chips.length) {
          hero.addEventListener('pointermove', function (e) {
            var r = hero.getBoundingClientRect();
            var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
            chips.forEach(function (c, i) {
              var f = 14 + i * 8;
              c.style.transform = 'translate(' + px * f + 'px,' + py * f + 'px)';
            });
          });
        }
      }

      // Canvas particle constellation behind the hero.
      safe('hero-canvas', function () { initHeroCanvas(hero); });
    });
  }

  function initHeroCanvas(hero) {
    if (!hero.querySelector) return;
    var cv = document.createElement('canvas');
    cv.className = 'fm-hero__canvas';
    cv.setAttribute('aria-hidden', 'true');
    cv.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:0;pointer-events:none;opacity:.85';
    // keep content above the canvas
    var inner = hero.querySelector('.fm-hero__in');
    if (inner) inner.style.position = 'relative';
    if (getComputedStyle(hero).position === 'static') hero.style.position = 'relative';
    hero.insertBefore(cv, hero.firstChild);

    var ctx = cv.getContext('2d'), W = 0, H = 0, parts = [], raf = null, dpr = Math.min(window.devicePixelRatio || 1, 2);
    var mouse = { x: -9999, y: -9999 };
    function seed() { parts = []; if (!W || !H) return; var n = Math.min(70, Math.floor(W * H / (30000 * dpr))); for (var i = 0; i < n; i++) parts.push({ x: Math.random() * W, y: Math.random() * H, vx: (Math.random() - 0.5) * 0.16 * dpr, vy: (Math.random() - 0.5) * 0.16 * dpr, r: (Math.random() * 1.5 + 0.7) * dpr }); }
    // Size can be 0 at init before layout settles — measure robustly and re-seed
    // whenever the hero actually has dimensions (ResizeObserver, else rAF retry).
    function sizeAndSeed() {
      var w = hero.offsetWidth || hero.clientWidth, h = hero.offsetHeight || hero.clientHeight;
      if (!w || !h) return false;
      W = cv.width = w * dpr; H = cv.height = h * dpr; seed(); return true;
    }
    if (!sizeAndSeed()) {
      if (window.ResizeObserver) {
        var ro = new ResizeObserver(function () { if (sizeAndSeed()) ro.disconnect(); });
        ro.observe(hero);
      } else {
        var tries = 0, retry = function () { if (sizeAndSeed() || ++tries > 30) return; requestAnimationFrame(retry); };
        requestAnimationFrame(retry);
      }
    }
    window.addEventListener('resize', function () { sizeAndSeed(); });
    if (finePointer) {
      hero.addEventListener('pointermove', function (e) { var r = cv.getBoundingClientRect(); mouse.x = (e.clientX - r.left) * dpr; mouse.y = (e.clientY - r.top) * dpr; });
      hero.addEventListener('pointerleave', function () { mouse.x = mouse.y = -9999; });
    }
    var DIST = 120 * dpr;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i], dx = p.x - mouse.x, dy = p.y - mouse.y, d = Math.sqrt(dx * dx + dy * dy);
        if (d < 110 * dpr && d > 0) { p.x += dx / d * 1.1; p.y += dy / d * 1.1; }
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.283); ctx.fillStyle = 'rgba(37,99,235,.45)'; ctx.fill();
        for (var j = i + 1; j < parts.length; j++) {
          var q = parts[j], ex = p.x - q.x, ey = p.y - q.y, ed = Math.sqrt(ex * ex + ey * ey);
          if (ed < DIST) { ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y); ctx.strokeStyle = 'rgba(34,211,238,' + (0.15 * (1 - ed / DIST)) + ')'; ctx.lineWidth = dpr; ctx.stroke(); }
        }
      }
      raf = requestAnimationFrame(draw);
    }
    draw();
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (e) { if (e.isIntersecting) { if (!raf) draw(); } else if (raf) { cancelAnimationFrame(raf); raf = null; } });
      }, { threshold: 0 }).observe(hero);
    }
  }

  /* ----------------------------------------------------------------------
     12. Subtle 3D tilt on product cards (desktop, motion-on, skin-on only).
     ---------------------------------------------------------------------- */
  function initSkinTilt() {
    if (document.documentElement.getAttribute('data-fm-skin') !== 'on') return;
    if (!motionOK || !finePointer) return;
    function bind(scope) {
      (scope || document).querySelectorAll('.product-item:not([data-fm-tilt])').forEach(function (card) {
        card.setAttribute('data-fm-tilt', '1');
        var inner = card.querySelector('.card') || card;
        inner.style.transformStyle = 'preserve-3d';
        card.addEventListener('pointermove', function (e) {
          var r = card.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
          inner.style.transform = 'perspective(900px) rotateY(' + (px * 5) + 'deg) rotateX(' + (-py * 5) + 'deg) translateY(-6px)';
        });
        card.addEventListener('pointerleave', function () {
          inner.style.transition = 'transform .5s cubic-bezier(.34,1.56,.5,1)';
          inner.style.transform = '';
          setTimeout(function () { inner.style.transition = ''; }, 500);
        });
      });
    }
    bind(document);
    var grid = document.getElementById('CollectionProductGrid');
    if (grid && window.MutationObserver) {
      new MutationObserver(function () { safe('tilt-rebind', function () { bind(grid); }); }).observe(grid, { childList: true, subtree: true });
    }
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  ready(function () {
    safe('darkToggle', initDarkToggle);
    safe('focusVisible', initFocusVisible);
    safe('navKeyboard', initNavKeyboard);
    safe('wishlistHearts', initWishlistHearts);
    safe('shipbar', initShipbar);
    safe('cartBridge', initCartBridge);
    safe('lowStockLive', initLowStockLive);
    safe('countdowns', initCountdowns);
    safe('fbt', initFBT);
    safe('skinReveal', initSkinReveal);
    safe('fmHero', initFmHero);
    safe('skinTilt', initSkinTilt);
  });

  // Re-inject hearts after collection AJAX re-renders.
  document.addEventListener('fm:collection:rerendered', function () { safe('rehearts', function () { injectHearts(document); }); });
  if (window.MutationObserver) {
    var grid = document.getElementById('CollectionProductGrid');
    if (grid) {
      var mo = new MutationObserver(function () { safe('grid-hearts', function () { injectHearts(grid); }); });
      mo.observe(grid, { childList: true, subtree: true });
    }
  }
})();
